import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../utils/axios';

// High Risk Work Activities (HRWA) — the catalog of high-risk construction work
// items (seeded with the national 18) plus the WHS/SWMS section content each one
// owns and the trades it applies to. Backs the superadmin HRWA editor, the job-
// creation activity picker, and the live job-page toggle. Maps to the backend
// routes mounted at /v1/hrwa (see wesafe-backend src/routes/hrwa.js).

const unwrap = (response: any) => response.data?.data ?? response.data;

export interface HrwaSection {
  id: number;
  hrwaActivityId: number;
  docType: 'whs' | 'swms';
  title: string | null;
  content: string | null;
  orderId: number;
}

export interface HrwaActivity {
  id: number;
  code: string;
  title: string;
  description: string | null;
  orderId: number;
  deleted?: number;
  // Present on the detailed catalog fetch (?detailed=1) and single-activity get.
  whsSections?: HrwaSection[];
  swmsSections?: HrwaSection[];
  professionListIds?: number[];
}

export const hrwaKeys = {
  all: ['hrwa'] as const,
  list: (detailed = false) => [...hrwaKeys.all, 'list', detailed] as const,
  detail: (id: string | number) => [...hrwaKeys.all, 'detail', String(id)] as const,
};

// The bare catalog — enough for the job wizard + job-page checklist.
export function useHrwaActivities(enabled = true) {
  return useQuery({
    queryKey: hrwaKeys.list(false),
    enabled,
    staleTime: 60_000,
    queryFn: async () => unwrap(await axios.get('/v1/hrwa/activities')) as HrwaActivity[],
  });
}

// The catalog with each activity's owned sections + applicability — the editor.
export function useHrwaActivitiesDetailed(enabled = true) {
  return useQuery({
    queryKey: hrwaKeys.list(true),
    enabled,
    queryFn: async () =>
      unwrap(await axios.get('/v1/hrwa/activities?detailed=1')) as HrwaActivity[],
  });
}

function useInvalidateHrwa() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: hrwaKeys.all });
}

// ---- catalog mutations ----------------------------------------------------

export function useCreateHrwaActivity() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async (body: { title: string; description?: string; code?: string }) =>
      unwrap(await axios.post('/v1/hrwa/activities', body)) as HrwaActivity,
    onSuccess: invalidate,
  });
}

export function useUpdateHrwaActivity() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: number;
      title?: string;
      description?: string | null;
      orderId?: number;
    }) => unwrap(await axios.put(`/v1/hrwa/activities/${id}`, body)) as HrwaActivity,
    onSuccess: invalidate,
  });
}

export function useDeleteHrwaActivity() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async (id: number) => unwrap(await axios.delete(`/v1/hrwa/activities/${id}`)),
    onSuccess: invalidate,
  });
}

// ---- activity-owned sections ----------------------------------------------

export function useCreateHrwaSection() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async ({
      activityId,
      ...body
    }: {
      activityId: number;
      docType: 'whs' | 'swms';
      title?: string;
      content?: string;
    }) =>
      unwrap(await axios.post(`/v1/hrwa/activities/${activityId}/sections`, body)) as HrwaSection,
    onSuccess: invalidate,
  });
}

export function useUpdateHrwaSection() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async ({
      sectionId,
      ...body
    }: {
      sectionId: number;
      title?: string | null;
      content?: string;
      orderId?: number;
    }) => unwrap(await axios.put(`/v1/hrwa/sections/${sectionId}`, body)) as HrwaSection,
    onSuccess: invalidate,
  });
}

export function useDeleteHrwaSection() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async (sectionId: number) =>
      unwrap(await axios.delete(`/v1/hrwa/sections/${sectionId}`)),
    onSuccess: invalidate,
  });
}

// ---- applicability (which trades an activity's SWMS section reaches) -------

export function useSetHrwaProfessions() {
  const invalidate = useInvalidateHrwa();
  return useMutation({
    mutationFn: async ({
      activityId,
      professionListIds,
    }: {
      activityId: number;
      professionListIds: number[];
    }) =>
      unwrap(
        await axios.put(`/v1/hrwa/activities/${activityId}/professions`, { professionListIds })
      ) as number[],
    onSuccess: invalidate,
  });
}
